import { apiClient } from "@/services/apiClient";

/**
 * The emisor block of every CFE: what lands in <CdgDGISucur>, <DomFiscal>,
 * <Ciudad>, <Departamento> and <RznSoc>.
 *
 * These were captured once at company creation and, until now, unreachable
 * afterwards — which is why correcting a tenant's sucursal needed an operator
 * with a script. See the approved spec "Editable emisor fiscal data".
 */
export interface FiscalIdentity {
  cdgDGISucur: number;
  /** <DomFiscal> */
  address: string | null;
  /** <Ciudad> */
  city: string | null;
  /** <Departamento> */
  department: string | null;
  /** <RznSoc> — refreshable from DGI, never hand-typed. */
  legalName: string | null;
  /** Contact details. Not CFE fields, but part of the same DGI comparison so
   *  the settings page needs only one "traer de DGI". */
  phone: string | null;
  contactEmail: string | null;
  rut: string;
}

/**
 * What DGI holds right now, for side-by-side comparison. Never applied
 * automatically: on sucursal DGI is authoritative and we go stale, but on
 * domicilio DGI sometimes has nothing and the stored value is the better one.
 */
export interface DgiFiscalIdentity {
  available: boolean;
  legalName: string | null;
  address: string | null;
  city: string | null;
  department: string | null;
  cdgDGISucur: number | null;
  phone: string | null;
  contactEmail: string | null;
  fetchedAt: string;
}

export interface FiscalIdentityUpdate {
  cdgDGISucur?: number;
  address?: string | null;
  city?: string | null;
  department?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
}

/** The tenant's own emisor block. */
export function getFiscalIdentity(): Promise<FiscalIdentity> {
  return apiClient
    .get<FiscalIdentity>("/tenants/current/fiscal-identity")
    .then((res) => res.data);
}

export function getFiscalIdentityFromDgi(): Promise<DgiFiscalIdentity> {
  return apiClient
    .get<DgiFiscalIdentity>("/tenants/current/fiscal-identity/dgi")
    .then((res) => res.data);
}

export function updateFiscalIdentity(
  updates: FiscalIdentityUpdate,
): Promise<FiscalIdentity> {
  return apiClient
    .patch<FiscalIdentity>("/tenants/current/fiscal-identity", updates)
    .then((res) => res.data);
}

/** An accountant acting on a cartera client. */
export function getAccountantTenantFiscalIdentity(
  tenantId: string,
): Promise<FiscalIdentity> {
  return apiClient
    .get<FiscalIdentity>(
      `/dashboard/accountant/tenants/${tenantId}/fiscal-identity`,
    )
    .then((res) => res.data);
}

export function getAccountantTenantFiscalIdentityFromDgi(
  tenantId: string,
): Promise<DgiFiscalIdentity> {
  return apiClient
    .get<DgiFiscalIdentity>(
      `/dashboard/accountant/tenants/${tenantId}/fiscal-identity/dgi`,
    )
    .then((res) => res.data);
}

export function updateAccountantTenantFiscalIdentity(
  tenantId: string,
  updates: FiscalIdentityUpdate,
): Promise<FiscalIdentity> {
  return apiClient
    .patch<FiscalIdentity>(
      `/dashboard/accountant/tenants/${tenantId}/fiscal-identity`,
      updates,
    )
    .then((res) => res.data);
}
